import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { atributosSoloMira, useSoporteSoloMira } from '@/features/mini-prixia/soporte-solo-mira';
import { Checkbox } from '@/components/ui/checkbox';
import { Cifra, Cifras } from '@/components/ui/cifras';
import { EstadoError } from '@/components/ui/estado-vacio';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { cantidadDe, formatoCantidad, formatoPrecio } from '@/lib/utils/formato-precio';
import { priceProposalsApi } from '../services/price-proposals-api';
import { partirEnLotes, sumarLotes } from '../utils/lotes';
import type {
  DestinoHoy,
  FiltroBandeja,
  HojaDeVuelta,
  ResultadoDeLote,
} from '../types/price-proposals.types';

/**
 * La hoja de la vuelta (épica H, plan 2, tarea 5): una decisión por estrategia
 * vencida, no una por producto. Maqueta: artboards `D-Vuelta` y `D-Resultado`.
 *
 * Aprobar en lote NO incluye las filas sin precio posible (ahí hay que elegir
 * la salida, en Por decidir) ni las que el precio o el costo de abajo ya cambió.
 * Ésas no las recalcula nadie (P2-D6): se eligen para dejarlas como están. Y
 * todo va en pedidos de 200 (el tope del servidor), sumando los resultados.
 */

interface VueltaSheetProps {
  /** null = cerrada. */
  strategyApplicationId: string | null;
  onClose: () => void;
  /**
   * Revisión t5 (m4): algo se resolvió en la hoja. La página refresca Hoy y
   * Por decidir, que si no quedarían mostrando lo de antes.
   */
  onResuelto?: () => void;
  onIrA: (
    destino: DestinoHoy,
    strategyApplicationId: string | null,
    filtro?: FiltroBandeja
  ) => void;
}

// El formato único del módulo (pulido, spec 232).
const num = formatoCantidad;
const cuenta = cantidadDe;
const plata = formatoPrecio;

// Fecha larga a propósito («vence el 30 de septiembre») y en la zona del
// negocio; `formatoFecha` no acepta zona horaria. Ronda final (B9).
const OPCIONES_DE_FECHA_LARGA = {
  day: 'numeric',
  month: 'long',
  timeZone: 'America/La_Paz',
} as const;

function fechaLarga(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('es-BO', OPCIONES_DE_FECHA_LARGA); // pulido: permitido — fecha larga a propósito y zona del negocio (B9).
}

const CAMBIO_DESPUES =
  'El precio (o el costo) cambió desde que se propuso: no vuelve sola. Dejala como está.';

export function VueltaSheet({
  strategyApplicationId,
  onClose,
  onIrA,
  onResuelto,
}: VueltaSheetProps) {
  // Soporte («Entrar como») en una demo sólo mira: no aprueba ni descarta nada.
  const soloMira = useSoporteSoloMira();
  const [hoja, setHoja] = useState<HojaDeVuelta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elegidas, setElegidas] = useState<Set<string>>(new Set());
  const [trabajando, setTrabajando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoDeLote | null>(null);

  /** La hoja que está abierta: un lote que termina tarde no pisa otra hoja. */
  const abierta = useRef<string | null>(strategyApplicationId);

  const cargar = useCallback(async (id: string, sigueViva: () => boolean = () => true) => {
    setError(null);
    try {
      const h = await priceProposalsApi.vuelta(id);
      if (!sigueViva()) return;
      setHoja(h);
      // Arrancan elegidas las que se pueden aprobar. Las que cambiaron se
      // eligen a mano, sólo para dejarlas como están (P2-D6).
      setElegidas(new Set(h.aprobables));
    } catch (e) {
      if (!sigueViva()) return;
      // 27-sep (t4 C8): el mensaje del servidor (un 404 de otra empresa dice
      // qué pasó), como la bandeja.
      setError(mensajeDeError(e, 'No se pudo cargar la vuelta. Probá de nuevo en un momento.'));
    }
  }, []);

  useEffect(() => {
    // 27-sep (t4 C9): si se abre la hoja de A y enseguida la de B, la respuesta
    // de A no pisa la de B (mismo patrón que hoy-tab.tsx:57-70).
    let viva = true;
    abierta.current = strategyApplicationId;
    setHoja(null);
    setResultado(null);
    if (strategyApplicationId) void cargar(strategyApplicationId, () => viva);
    return () => {
      viva = false;
    };
  }, [strategyApplicationId, cargar]);

  const aprobables = useMemo(() => new Set(hoja?.aprobables ?? []), [hoja]);
  const descartables = useMemo(() => new Set(hoja?.descartables ?? []), [hoja]);
  /** Aprobar manda sólo las elegidas que se pueden aprobar. */
  const aAprobar = useMemo(
    () => [...elegidas].filter((id) => aprobables.has(id)),
    [elegidas, aprobables]
  );

  const alternar = (id: string) =>
    setElegidas((prev) => {
      const nuevo = new Set(prev);
      if (nuevo.has(id)) nuevo.delete(id);
      else nuevo.add(id);
      return nuevo;
    });

  const resolver = async (accion: 'aprobar' | 'descartar') => {
    if (!strategyApplicationId || !hoja) return;
    // Dejar como están: todas las elegidas (también las que cambiaron).
    const ids = accion === 'aprobar' ? aAprobar : [...elegidas];
    if (ids.length === 0) return;
    const id = strategyApplicationId;
    const sigueAbierta = () => abierta.current === id;
    setTrabajando(true);
    // Fuera del try: si un pedido falla a la mitad, lo que ya se resolvió se
    // muestra igual y la hoja se recarga.
    const resultados: ResultadoDeLote[] = [];
    try {
      for (const lote of partirEnLotes(ids)) {
        if (accion === 'aprobar') {
          // 27-sep (t4 C1): el precio que mostraba cada fila, como la bandeja
          // (saneamiento T7): si la fila cambió mientras se miraba, no se aplica.
          const enLote = new Set(lote);
          const vistos = Object.fromEntries(
            hoja.filas.filter((f) => enLote.has(f.id)).map((f) => [f.id, f.priceAfter])
          );
          resultados.push(await priceProposalsApi.aprobarLote(lote, vistos));
        } else {
          resultados.push(await priceProposalsApi.descartarLote(lote));
        }
      }
      onResuelto?.();
      if (!sigueAbierta()) return;
      setResultado(sumarLotes(resultados));
      await cargar(id, sigueAbierta);
    } catch (e) {
      if (resultados.length > 0) onResuelto?.();
      if (!sigueAbierta()) return;
      if (resultados.length > 0) {
        setResultado(sumarLotes(resultados));
        await cargar(id, sigueAbierta);
      }
      setError(
        mensajeDeError(
          e,
          'No se pudo completar. Lo que ya se aplicó queda aplicado; probá de nuevo.'
        )
      );
    } finally {
      setTrabajando(false);
    }
  };

  const r = hoja?.resumen ?? null;
  const termino = fechaLarga(hoja?.validUntil ?? null);
  const pausadas = r?.devueltosAPausadas ?? [];

  return (
    <Sheet open={strategyApplicationId !== null} onOpenChange={(o) => !o && onClose()}>
      {/* Tarea 6 (navegador): el componente base no trae margen; sin p-6 el
          contenido pegaba contra los bordes y el título corría bajo la X. */}
      <SheetContent side="right" className="w-full overflow-y-auto p-6 sm:max-w-3xl">
        <SheetHeader className="pr-8">
          <SheetTitle>{hoja ? `Venció «${hoja.strategyName}»` : 'La vuelta'}</SheetTitle>
          <SheetDescription>
            {termino ? `Terminó el ${termino}. ` : ''}
            Cada producto vuelve al precio que tenía antes de que ella lo tocara, dentro de lo que
            permiten tus reglas de hoy.
          </SheetDescription>
        </SheetHeader>

        {error && (
          <EstadoError
            className="mt-4"
            onReintentar={
              strategyApplicationId
                ? () =>
                    void cargar(
                      strategyApplicationId,
                      () => abierta.current === strategyApplicationId
                    )
                : undefined
            }
          >
            {error}
          </EstadoError>
        )}

        {!hoja && !error && <Skeleton className="mt-6 h-40 w-full" />}

        {hoja && (
          <div className="mt-6 space-y-5">
            {/* 27-sep (t4 C7): lo que espera sale de la hoja, en vivo; el resto
                es lo que pasó al vencer, congelado en el cierre. */}
            <p className="text-sm">
              <span className="text-lg font-semibold">{num(hoja.aprobables.length)}</span>{' '}
              {hoja.aprobables.length === 1 ? 'espera' : 'esperan'} tu sí
            </p>
            {r && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Al vencer:</p>
                <Cifras aria-label="Qué pasó al vencer">
                  {/* Tarea 6 (navegador): «1 volvieron» se leía mal; en singular con 1. */}
                  <Contador valor={r.sinPrecioPosible} texto="hay que elegir" />
                  <Contador
                    valor={r.devueltos}
                    texto={
                      r.devueltos === 1 ? 'volvió a su estrategia' : 'volvieron a su estrategia'
                    }
                  />
                  <Contador
                    valor={r.sinCambio + r.sinReferencia}
                    texto={
                      r.sinCambio + r.sinReferencia === 1 ? 'queda como está' : 'quedan como están'
                    }
                  />
                  <Contador
                    valor={r.loCambioOtraPersona}
                    texto={
                      r.loCambioOtraPersona === 1
                        ? 'lo cambió otra persona'
                        : 'los cambió otra persona'
                    }
                  />
                </Cifras>
              </div>
            )}

            {pausadas.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {pausadas
                  .map(
                    (d) =>
                      `${d.productos === 1 ? '1 producto volvió' : `${num(d.productos)} productos volvieron`} a «${d.name}»`
                  )
                  .join('; ')}
                , que está pausada o todavía no empezó: su precio se decide acá.
              </p>
            )}

            {hoja.sinPrecioPosible > 0 && (
              <div className="flex items-center justify-between gap-3 rounded-md border border-bloqueo-borde bg-bloqueo-suave p-3 text-sm">
                <span>
                  {hoja.sinPrecioPosible === 1
                    ? '1 producto no tiene un precio que cumpla todas tus reglas: se elige uno por uno.'
                    : `${num(hoja.sinPrecioPosible)} productos no tienen un precio que cumpla todas tus reglas: se eligen uno por uno.`}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    onIrA('BANDEJA', hoja.strategyApplicationId, { sinPrecioPosible: true })
                  }
                >
                  Elegir en Por decidir
                </Button>
              </div>
            )}

            {r && r.muestraLoCambioOtraPersona.length > 0 && (
              <details className="rounded-md border p-3 text-sm">
                <summary className="cursor-pointer font-medium">
                  {r.loCambioOtraPersona === 1
                    ? '1 precio no se tocó: alguien lo cambió después de la estrategia'
                    : `${num(r.loCambioOtraPersona)} precios no se tocaron: alguien los cambió después de la estrategia`}
                </summary>
                <ul className="mt-2 space-y-1 text-muted-foreground">
                  {r.muestraLoCambioOtraPersona.map((m) => (
                    <li key={m.sku}>
                      {m.name} ({m.sku}): la estrategia lo dejó en {plata(m.ultimoDeLaEstrategia)} y
                      hoy está en {plata(m.hoy)}. Lo cambió otra persona.
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {hoja.filas.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No queda ningún precio esperando volver.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Producto</TableHead>
                    <TableHead className="text-right">Hoy (Bs)</TableHead>
                    <TableHead className="text-right">Vuelve a (Bs)</TableHead>
                    <TableHead>De dónde sale</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hoja.filas.map((f) => (
                    <TableRow
                      key={f.id}
                      data-testid="fila-vuelta"
                      className={f.sinPrecioPosible ? 'bg-bloqueo-suave' : undefined}
                    >
                      <TableCell>
                        {descartables.has(f.id) && (
                          <Checkbox
                            checked={elegidas.has(f.id)}
                            onCheckedChange={() => alternar(f.id)}
                            aria-label={`Elegir ${f.productName}`}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{f.productName}</div>
                        <div className="text-xs text-muted-foreground">{f.sku}</div>
                      </TableCell>
                      {/* 27-sep (t4 C4): el precio de hoy, no el de cuando se propuso. */}
                      <TableCell className="text-right tabular-nums">
                        {plata(f.precioActual)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {f.sinPrecioPosible ? 'a elegir' : plata(f.priceAfter)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {f.sinPrecioPosible
                          ? 'Conflicto entre reglas: se elige en Por decidir.'
                          : f.desactualizada || f.costoCambio
                            ? CAMBIO_DESPUES
                            : (f.explicacionDeVuelta ?? '—')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {hoja.recortadas > 0 && (
              <p className="text-sm text-muted-foreground">
                Se muestran {num(hoja.filas.length)} de {num(hoja.total)}. Al resolver éstos, se
                cargan los que siguen.
              </p>
            )}

            {resultado && (
              <div className="rounded-md border p-3 text-sm" aria-label="Resultado">
                <p className="font-medium">
                  {/* Revisión t5 (m5): el mismo vocabulario que el botón. */}
                  {cuenta(resultado.porDesenlace.APLICADA ?? 0, 'aplicado', 'aplicados')} ·{' '}
                  {cuenta(
                    resultado.porDesenlace.DESCARTADA ?? 0,
                    'dejado como está',
                    'dejados como están'
                  )}
                </p>
                <ul className="mt-1 space-y-1 text-muted-foreground">
                  {resultado.resultados
                    .filter((x) => x.motivo)
                    .map((x) => (
                      <li key={x.id}>{x.motivo}</li>
                    ))}
                </ul>
              </div>
            )}

            {/* Tarea 6 (navegador): sin nada que resolver, no quedan «Aprobar 0»
                ni «0 de 0 seleccionados». */}
            {descartables.size > 0 && (
              <div className="sticky bottom-0 space-y-2 border-t bg-background pt-3">
                <p className="text-sm">
                  {num(elegidas.size)} de {num(descartables.size)} seleccionados
                </p>
                <p className="text-xs text-muted-foreground">
                  Al aprobar se revisa cada precio de nuevo: si alguno se movió desde que se
                  propuso, esa fila no se aplica y te dice por qué.
                </p>
                <div className="flex gap-2">
                  <Button
                    disabled={trabajando || aAprobar.length === 0}
                    {...atributosSoloMira(soloMira)}
                    onClick={() => void resolver('aprobar')}
                  >
                    {trabajando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Aprobar {num(aAprobar.length)}
                  </Button>
                  <Button
                    variant="outline"
                    disabled={trabajando || elegidas.size === 0}
                    {...atributosSoloMira(soloMira)}
                    onClick={() => void resolver('descartar')}
                  >
                    Dejar como están
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/** Una cifra de «Al vencer», con la pieza compartida (pulido: `Cifras`). */
function Contador({ valor, texto }: { valor: number; texto: string }) {
  return <Cifra valor={valor} rotulo={texto} chica />;
}
